import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCopy } from 'react-icons/fa';
import toast from 'react-hot-toast';
import type { TrackPermissions } from '../../types/track';
import type { Track } from '../../services/trackTypes';
import { updateTrackMetadata } from '../../services/trackService';

interface PromoPitchesSectionProps {
  promoPitch: { en: string; native: string };
  setPromoPitch: (p: { en: string; native: string }) => void;
  secondLang: string | null;
  setSecondLang: (lang: string | null) => void;
  trackId: string;
  permissions?: TrackPermissions;
}

const PromoPitchesSection: React.FC<PromoPitchesSectionProps> = ({
  promoPitch,
  setPromoPitch,
  secondLang,
  setSecondLang,
  trackId,
  permissions,
}) => {
  const { t } = useTranslation();

  // Character count for English pitch
  const pitchEnLength = promoPitch.en.length;
  const pitchEnLimit = 280; // Spotify pitch limit

  // Status tracking for both pitches
  const [pitchEnStatus, setPitchEnStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [pitchNativeStatus, setPitchNativeStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  // Single timeout ref for debounced saving (both pitches share one request)
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Dirty flags to track which pitch fields changed since the last save
  const [pitchEnDirty, setPitchEnDirty] = useState(false);
  const [pitchNativeDirty, setPitchNativeDirty] = useState(false);

  // Debounced save: whichever pitch field(s) changed are persisted together in ONE
  // atomic PATCH /metadata request. This avoids firing two parallel /pitch requests.
  useEffect(() => {
    if (!pitchEnDirty && !pitchNativeDirty) return;
    if (saveTimeout.current) clearTimeout(saveTimeout.current);

    if (pitchEnDirty) setPitchEnStatus('saving');
    if (pitchNativeDirty) setPitchNativeStatus('saving');

    saveTimeout.current = setTimeout(async () => {
      const metadata: Partial<Track> = {};
      if (pitchEnDirty) metadata.pitchEn = promoPitch.en;
      if (pitchNativeDirty) metadata.pitchNative = promoPitch.native;
      try {
        await updateTrackMetadata(trackId, metadata);
        if (pitchEnDirty) {
          setPitchEnStatus('saved');
          setPitchEnDirty(false);
          setTimeout(() => setPitchEnStatus('idle'), 1200);
        }
        if (pitchNativeDirty) {
          setPitchNativeStatus('saved');
          setPitchNativeDirty(false);
          setTimeout(() => setPitchNativeStatus('idle'), 1200);
        }
      } catch {
        if (pitchEnDirty) setPitchEnStatus('error');
        if (pitchNativeDirty) setPitchNativeStatus('error');
      }
    }, 1000);

    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
    };
  }, [promoPitch.en, promoPitch.native, trackId, pitchEnDirty, pitchNativeDirty]);

  // Handle pitch copy
  const handleCopyPitch = useCallback((text: string, successMessage: string) => {
    navigator.clipboard.writeText(text);
    toast.success(successMessage);
  }, []);

  // Handle pitch change
  const handlePitchChange = useCallback((lang: 'en' | 'native', value: string) => {
    setPromoPitch({ ...promoPitch, [lang]: value });
    if (lang === 'en') {
      setPitchEnDirty(true);
    } else {
      setPitchNativeDirty(true);
    }
  }, [setPromoPitch, promoPitch]);

  return (
    <div className="mb-8">
      {/* English Pitch */}
             <div className="relative">
               <textarea
                 className="w-full rounded-md p-2 text-sm focus:ring-primary-500 focus:border-primary-500 pr-10 border"
                 style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', borderColor: 'var(--color-border)' }}
                 rows={3}
                 value={promoPitch.en}
                 onChange={permissions?.canEditMetadata ? (e => handlePitchChange('en', e.target.value)) : undefined}
                 placeholder={permissions?.canEditMetadata ? t('promo.pitchPlaceholder') : 'No pitch available'}
                 readOnly={!permissions?.canEditMetadata}
                 data-testid="english-pitch-textarea"
               />
        <button
          type="button"
          className="absolute top-2 right-2 hover:text-blue-600 w-6 h-6 flex items-center justify-center"
          style={{ color: 'var(--color-muted)' }}
          onClick={() => handleCopyPitch(promoPitch.en, t('promo.copied'))}
          title={t('promo.copy')}
          aria-label={t('promo.copy')}
        >
          <FaRegCopy />
        </button>
      </div>
      
      <div className="text-xs h-5" style={{ color: 'var(--color-muted)' }}>
        {pitchEnStatus === 'saving' && t('notes.saving')}
        {pitchEnStatus === 'saved' && t('notes.saved')}
        {pitchEnStatus === 'error' && t('notes.error')}
      </div>
      
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs" style={{ color: 'var(--color-muted)' }}>{t('promo.spotifyPitchLimit')}</span>
        <span className={`text-xs font-mono`} style={{ color: pitchEnLength > pitchEnLimit ? '#ca8a04' : 'var(--color-muted)' }}>
          {t('promo.characterCount', { current: pitchEnLength, max: pitchEnLimit })}
        </span>
      </div>
      
      {pitchEnLength > pitchEnLimit && (
        <div className="text-xs text-yellow-600 mt-1">
          {t('promo.spotifyPitchLimit')}
        </div>
      )}

      {/* Native Language Pitch - Always visible */}
      <div className="flex flex-col gap-1 w-full mt-2">
        <div className="flex items-center gap-2">
          <span className="block text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            {t('promo.pitchLabelNative')}
          </span>
          {secondLang && (
            <button
              type="button"
              className="text-xs hover:text-gray-700 ml-2"
              style={{ color: 'var(--color-muted)' }}
              onClick={() => setSecondLang(null)}
            >
              {t('buttons.remove')}
            </button>
          )}
        </div>

        <div className="relative">
          <textarea
            className="w-full rounded-md p-2 text-sm focus:ring-primary-500 focus:border-primary-500 pr-10 border"
            style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', borderColor: 'var(--color-border)' }}
            rows={3}
            value={promoPitch.native}
            onChange={permissions?.canEditMetadata ? (e => handlePitchChange('native', e.target.value)) : undefined}
            placeholder={permissions?.canEditMetadata ? t('promo.pitchPlaceholderNative') : 'No pitch available'}
            readOnly={!permissions?.canEditMetadata}
            data-testid="native-pitch-textarea"
          />
          <button
            type="button"
            className="absolute top-2 right-2 hover:text-blue-600 w-6 h-6 flex items-center justify-center"
            style={{ color: 'var(--color-muted)' }}
            aria-label="Copy pitch to clipboard"
            onClick={() => handleCopyPitch(promoPitch.native || '', 'Pitch copied!')}
          >
            <FaRegCopy size={18} />
          </button>
        </div>

        <div className="text-xs h-5" style={{ color: 'var(--color-muted)' }}>
          {pitchNativeStatus === 'saving' && t('notes.saving')}
          {pitchNativeStatus === 'saved' && t('notes.saved')}
          {pitchNativeStatus === 'error' && t('notes.error')}
        </div>
      </div>
    </div>
  );
};

export default PromoPitchesSection;